import gsap from "gsap";
import SplitText from "gsap/SplitText";
import ScrollTrigger from "gsap/ScrollTrigger";
import { hellopanda_func as $ } from '../../../assets/script/func.js';

// 플러그인 등록
gsap.registerPlugin(SplitText, ScrollTrigger);

ScrollTrigger.config({
  autoRefreshEvents: 'load, DOMContentLoaded',
});

/**
 * GSAP 실행
 */
const HELLOPANDA_GSAP = {
  isComplicated: false,
  resizeSet: new Set(),
  resizeWidth: window.innerWidth,
  resizeHeight: window.innerHeight,
  resizeHeightAllow: $.resizeHeightAllow,
  init: function() {

    const _ = this;

    document.addEventListener('DOMContentLoaded', function() {
  
      const containers = document.querySelectorAll('.wp-block-hellopanda-gsap-block:not(.wp-block-hellopanda-gsap-block-cover):not(.wp-block-hellopanda-gsap-block-pinspacer)');

      if (!containers.length) return; // container 없으면 종료

      containers.forEach(container => {
        container.dataset.gsapUid = 'gsap-' + Math.random().toString(36).substr(2, 9);
        _.gsap(container);
      });

      // 구독형 리사이즈 ( IOS 개선 )
      const handleResize = _.debounce(() => {
        if (_.checkResize()) {
          _.resizeNotify();
        }
      }, 300);
      window.addEventListener('resize', handleResize.bind(this));

      // 전역 resize 감지 구독
      _.resizeSubscribe(() => {
        _.refresh();
      });

      // 다크, 라이트모드 감시
      if (containers.length) {
        const target = document.documentElement;
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "data-theme-mode"
            ) {
              // window.scrollTo(0, 0);
              requestAnimationFrame(() => {
                setTimeout(() => {
                  _.colorRefresh();
                }, 100);
              });
            }
          });
        });
        observer.observe(target, {
          attributes: true,
          attributeFilter: ["data-theme-mode"], // 이 속성만 감시
        });
      }

      // 최상단 스크롤 인식
      let wasAtTop = window.scrollY === 0;
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(() => {
            const atTop = window.scrollY === 0;
            if (atTop && !wasAtTop) {
              _.pinInsideParentRefresh();
            }
            wasAtTop = atTop;
            ticking = false;
          });
        }
      }, { passive: true });

      // 전체 로드 후 리프레시 한번
      window.addEventListener('load', function() {
        if (window.scrollY > window.innerHeight) {
          _.onloadRefresh();
        }
      });

    });
  },

  debounce: function(fn, delay) {

    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };

  },

  // 진짜 resize 발생 여부 확인
  checkResize: function() {
    const _ = this;
    const w = window.innerWidth;
    const h = window.innerHeight;
    let changed = false;

    if (w !== _.resizeWidth) {
      _.resizeWidth = w;
      changed = true;
    } else if (Math.abs(h - _.resizeHeight) >= _.resizeHeightAllow) {
      _.resizeHeight = h;
      changed = true;
    }

    return changed;
  },

  // 모든 구독자에게 알림
  resizeNotify: function() {
    const _ = this;
    _.resizeSet.forEach(fn => fn(_.resizeWidth, _.resizeHeight));
  },

  // 모듈이 리사이즈 감지에 참여할 때
  resizeSubscribe: function(fn) {
    const _ = this;
    _.resizeSet.add(fn);
    return () => _.resizeSet.delete(fn);
  },

  getDataset: function(container) {

    const dataset = container.dataset;
    const gsapData = {};

    for (const key in dataset) {
      if (key.startsWith('gsap')) {
        // gsap 접두사 제거
        const name = key.replace(/^gsap/, '');
        // 첫 글자 소문자로 변환
        const prop = name.charAt(0).toLowerCase() + name.slice(1);

        // 값 변환: 숫자와 boolean 처리
        let value = dataset[key];
        if (!isNaN(value)) {
          value = parseFloat(value);
        } else if (value === 'true') {
          value = true;
        } else if (value === 'false') {
          value = false;
        }

        gsapData[prop] = value;
      }
    }

    return gsapData;

  },

  getToggleActions: function(dataset) {

    let result = '';
    // onEnter, onLeave, onEnterBack, onLeaveBack

    if (dataset.scrub) {
      return '';
    } else {
      switch (dataset.toggle) {
        case 'reverse':
          result = 'none none play none';
          break;
        case 'reverse-always':
          result = 'none reverse play none';
          break;
        case 'both':
          result = 'play reverse play reverse';
          break;
        case 'play-always':
          result = 'play none none reverse';
          break;
        default:
          result = 'play none none none';
          break;
      }
    }

    return result;

  },

  getStartOffset: function(dataset) {

    const val = dataset.startOffset;

    if (!val) return ''; // 값 없으면 빈 문자열

    if (isNaN(val)) {
      // 숫자가 아님 → %, px, vh 같은 단위가 붙은 값
      return `${val.includes('-') ? '-=' : '+='}${val.replace('-', '')}`;
    } else {
      // 숫자임
      const num = Number(val);
      return `${num >= 0 ? '+=' : '-='}${Math.abs(num)}`;
    }

  },

  getEndOffset: function(dataset) {

    const val = dataset.endOffset;

    if (!val) return ''; // 값 없으면 바로 종료

    if (isNaN(val)) {
      // 숫자가 아님 → 단위 포함된 문자열 (%, px, vh 등)
      return `${val.includes('-') ? '-=' : '+='}${val.replace('-', '')}`;
    } else {
      // 숫자임
      const num = Number(val);
      return `${num >= 0 ? '+=' : '-='}${Math.abs(num)}`;
    }

  },

  getFilter: function(target, dataset) {

    let filters = [];

    if (target === 'from') {
      if (dataset.fromFilterGray || dataset.fromFilterGray === 0) {
        filters.push(`grayscale(${dataset.fromFilterGray}%)`);
      } else if (dataset.toFilterGray || dataset.toFilterGray === 0) {
        filters.push(`grayscale(0%)`);
      }
      if (dataset.fromFilterBlur || dataset.fromFilterBlur === 0) {
        filters.push(`blur(${dataset.fromFilterBlur}px)`);
      } else if (dataset.ToFilterBlur || dataset.ToFilterBlur === 0) {
        filters.push(`blur(0px)`);
      }
      if (dataset.fromFilterBright || dataset.fromFilterBright === 0) {
        filters.push(`brightness(${dataset.fromFilterBright})`);
      } else if (dataset.toFilterBright || dataset.toFilterBright === 0) {
        filters.push(`brightness(1)`);
      }
    } else if (target === 'to') {
      if (dataset.toFilterGray || dataset.toFilterGray === 0) {
        filters.push(`grayscale(${dataset.toFilterGray}%)`);
      } else if (dataset.fromFilterGray || dataset.fromFilterGray === 0) {
        filters.push(`grayscale(0%)`);
      }
      if (dataset.toFilterBlur || dataset.toFilterBlur === 0) {
        filters.push(`blur(${dataset.toFilterBlur}px)`);
      } else if (dataset.fromFilterBlur || dataset.fromFilterBlur === 0) {
        filters.push(`blur(0px)`);
      }
      if (dataset.toFilterBright || dataset.toFilterBright === 0) {
        filters.push(`brightness(${dataset.toFilterBright})`);
      } else if (dataset.fromFilterBright || dataset.fromFilterBright === 0) {
        filters.push(`brightness(1)`);
      }
    }

    let filterString = filters.join(' ');

    return filterString;

  },

  zeroWithSameUnit: function(value) {

    if (value == null) return value;

    // 문자열이 아니면 문자열로 변환
    const str = String(value).trim();

    // 이미 0으로 해석되는 값이면 단위를 유지해서 반환
    const zeroMatch = str.match(/^([+-]?0*(?:\.0+)?)\s*([a-z%]*)$/i);
    if (zeroMatch) {
      const unit = zeroMatch[2] || '';
      return `0${unit}`;
    }

    // 숫자 + 단위 패턴 추출 (단위는 선택)
    const m = str.match(/^([+-]?\d*\.?\d+)\s*([a-z%]*)$/i);
    if (!m) {
      // 매치가 안 되면 원본을 그대로 돌려보내거나, 필요시 '0'로 강제
      return str;
    }

    const unit = m[2] || '';
    return `0${unit}`;

  },

  getOriginColor: function(color) {
    // var(--...) 패턴 체크 및 추출
    const match = color.match(/^var\((--[^)]+)\)$/);
    if (match) {
      // body 기준으로 계산된 값 가져오기
      const calcColor = getComputedStyle(document.body)
        .getPropertyValue(match[1])
        .trim();
      if (calcColor) return calcColor;
    }
    // var 아니면 그대로 반환
    return color;
  },

  setDataset: function(container, dataset) {

    const _ = this;

    const originStyle = getComputedStyle(container);

    const gsapFrom = {};
    const gsapTo = {};

    // zIndex
    if (dataset.zIndex !== undefined) {
      gsapFrom.zIndex = dataset.zIndex;
      gsapTo.zIndex = dataset.zIndex;
    }

    // x
    if (dataset.fromX !== undefined) {
      gsapFrom.x = dataset.fromX + '%';
      gsapTo.x = '0%';
    }
    if (dataset.toX !== undefined) {
      gsapFrom.x = gsapFrom.x ? gsapFrom.x : '0%' ;
      gsapTo.x = dataset.toX + '%';
    }

    // y
    if (dataset.fromY !== undefined) {
      gsapFrom.y = dataset.fromY + '%';
      gsapTo.y = '0%';
    }
    if (dataset.toY !== undefined) {
      gsapFrom.y = gsapFrom.y ? gsapFrom.y : '0%' ;
      gsapTo.y = dataset.toY + '%';
    }

    // scale
    if (dataset.fromScale !== undefined) {
      gsapFrom.scale = dataset.fromScale;
      gsapTo.scale = 1;
    }
    if (dataset.toScale !== undefined) {
      gsapFrom.scale = gsapFrom.scale != undefined ? gsapFrom.scale : 1 ;
      gsapTo.scale = dataset.toScale;
    }

    // scale X
    if (dataset.fromScaleX !== undefined) {
      gsapFrom.scaleX = dataset.fromScaleX;
      gsapTo.scaleX = 1;
    }
    if (dataset.toScaleX !== undefined) {
      gsapFrom.scaleX = gsapFrom.scaleX != undefined ? gsapFrom.scaleX : 1 ;
      gsapTo.scaleX = dataset.toScaleX;
    }

    // scale Y
    if (dataset.fromScaleY !== undefined) {
      gsapFrom.scaleY = dataset.fromScaleY;
      gsapTo.scaleY = 1;
    }
    if (dataset.toScaleY !== undefined) {
      gsapFrom.scaleY = gsapFrom.scaleY != undefined ? gsapFrom.scaleY : 1 ;
      gsapTo.scaleY = dataset.toScaleY;
    }

    // rotate
    if (dataset.fromRotate !== undefined) {
      gsapFrom.rotation = (360 * (isNaN(dataset.fromRotate) ? 1 : Number(dataset.fromRotate)));
      gsapTo.rotation = 0;
    }
    if (dataset.toRotate !== undefined) {
      gsapFrom.rotation = gsapFrom.rotation != undefined ? gsapFrom.rotation : 1 ;
      gsapTo.rotation = (360 * (isNaN(dataset.toRotate) ? 1 : Number(dataset.toRotate)));
    }

    // rotate X
    if (dataset.fromRotateX !== undefined) {
      gsapFrom.rotateX = (360 * (isNaN(dataset.fromRotateX) ? 1 : Number(dataset.fromRotateX)));
      gsapTo.rotateX = 0;
    }
    if (dataset.toRotateX !== undefined) {
      gsapFrom.rotateX = gsapFrom.rotateX != undefined ? gsapFrom.rotateX : 0 ;
      gsapTo.rotateX = (360 * (isNaN(dataset.toRotateX) ? 1 : Number(dataset.toRotateX)));
    }

    // rotate Y
    if (dataset.fromRotateY !== undefined) {
      gsapFrom.rotateY = (360 * (isNaN(dataset.fromRotateY) ? 1 : Number(dataset.fromRotateY)));
      gsapTo.rotateY = 0;
    }
    if (dataset.toRotateY !== undefined) {
      gsapFrom.rotateY = gsapFrom.rotateY != undefined ? gsapFrom.rotateY : 0 ;
      gsapTo.rotateY = (360 * (isNaN(dataset.toRotateY) ? 1 : Number(dataset.toRotateY)));
    }

    // skew X
    if (dataset.fromSkewX !== undefined) {
      gsapFrom.skewX = dataset.fromSkewX;
      gsapTo.skewX = 0;
    }
    if (dataset.toSkewX !== undefined) {
      gsapFrom.skewX = gsapFrom.skewX != undefined ? gsapFrom.skewX : 0 ;
      gsapTo.skewX = dataset.toSkewX;
    }

    // skew Y
    if (dataset.fromSkewY !== undefined) {
      gsapFrom.skewY = dataset.fromSkewY;
      gsapTo.skewY = 0;
    }
    if (dataset.toSkewY !== undefined) {
      gsapFrom.skewY = gsapFrom.skewY != undefined ? gsapFrom.skewY : 0 ;
      gsapTo.skewY = dataset.toSkewY;
    }

    // opacity
    if (dataset.fromOpacity !== undefined) {
      gsapFrom.opacity = dataset.fromOpacity;
      gsapTo.opacity = 1;
    }
    if (dataset.toOpacity !== undefined) {
      gsapFrom.opacity = gsapFrom.opacity != undefined ? gsapFrom.opacity : 1 ;
      gsapTo.opacity = dataset.toOpacity;
    }

    // clippath
    if (dataset.fromClippath !== undefined) {
      gsapFrom.clipPath = dataset.fromClippath;
      gsapTo.clipPath = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';
    }
    if (dataset.toClippath !== undefined) {
      gsapFrom.clipPath = gsapFrom.clipPath ? gsapFrom.clipPath : 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';
      gsapTo.clipPath = dataset.toClippath;
    }

    // filter
    if (dataset.fromFilterGray !== undefined || dataset.fromFilterBlur !== undefined || dataset.fromFilterBright !== undefined) {
      gsapFrom.filter = _.getFilter('from', dataset);
      gsapTo.filter = _.getFilter('to', dataset);
    }
    if (dataset.toFilterGray !== undefined || dataset.toFilterBlur !== undefined || dataset.toFilterBright !== undefined) {
      gsapFrom.filter = _.getFilter('from', dataset);
      gsapTo.filter = _.getFilter('to', dataset);
    }

    // text color
    if (dataset.fromTextColor !== undefined) {
      gsapFrom.color = () => _.getOriginColor(dataset.fromTextColor);
      gsapTo.color = originStyle.color;

    }
    if (dataset.toTextColor !== undefined) {
      gsapFrom.color = gsapFrom.color ? gsapFrom.color : originStyle.color ;
      gsapTo.color = () => _.getOriginColor(dataset.toTextColor);
    }

    // bg color
    if (dataset.fromBgColor !== undefined) {
      gsapFrom.backgroundColor = () => _.getOriginColor(dataset.fromBgColor);
      gsapTo.backgroundColor = originStyle.backgroundColor;
    }
    if (dataset.toBgColor !== undefined) {
      gsapFrom.backgroundColor = gsapFrom.backgroundColor ? gsapFrom.backgroundColor : originStyle.backgroundColor ;
      gsapTo.backgroundColor = () => _.getOriginColor(dataset.toBgColor);
    }

    // margin x
    if (dataset.fromMarginx !== undefined) {
      gsapFrom.marginLeft = dataset.fromMarginx;
      gsapFrom.marginRight = dataset.fromMarginx;
      gsapTo.marginLeft = 'auto';
      gsapTo.marginRight = 'auto';
    }
    if (dataset.toMarginx !== undefined) {
      if (!dataset.fromMarginx) {
        gsapTo.marginLeft = 'auto';
        gsapTo.marginRight = 'auto';
      }
      gsapTo.marginLeft = dataset.toMarginx;
      gsapTo.marginRight = dataset.toMarginx;
    }

    // radius
    if (dataset.fromRadius !== undefined) {
      gsapFrom.borderRadius = dataset.fromRadius;
      gsapTo.borderRadius = _.zeroWithSameUnit(dataset.fromRadius);
    }
    if (dataset.toRadius !== undefined) {
      gsapFrom.borderRadius = gsapFrom.borderRadius ? gsapFrom.borderRadius : _.zeroWithSameUnit(dataset.toRadius);
      gsapTo.borderRadius = dataset.toRadius;
    }

    // base set
    if (dataset.delay !== undefined) {
      gsapTo.delay = dataset.delay;
    }
    if (dataset.duration !== undefined) {
      gsapTo.duration = dataset.duration;
    }
    if (dataset.ease !== undefined) {
      gsapTo.ease = dataset.ease;
    }
    if (dataset.origin !== undefined) {
      gsapFrom.transformOrigin = dataset.origin;
      gsapTo.transformOrigin = dataset.origin;
    }

    gsapFrom.immediateRender = dataset.render ? true : false ;
    gsapTo.immediateRender = dataset.render ? true : false ;

    return {
      gsapFrom,
      gsapTo
    }

  },

  getGsapVars: function(item) {

    const type = item.dataset.gsapType || '';
    const direction = item.dataset.gsapDirection || '';
    const clippath = item.dataset.gsapFromClippath || '';

    let fromVars = { xPercent: 0, yPercent: 0 };
    let toVars   = { xPercent: 0, yPercent: 0 };

    if (clippath) {
      delete fromVars.xPercent;
      delete fromVars.yPercent;
      delete toVars.xPercent;
      delete toVars.yPercent;
      fromVars.clipPath = clippath;
      toVars.clipPath = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)';
      return { fromVars, toVars };
    }

    switch (direction) {
      case 'left-right':
        if (type === 'in') { fromVars.xPercent = -100; toVars.xPercent = 0; }
        else { fromVars.xPercent = 0; toVars.xPercent = 100; } // out → 밖으로
        break;

      case 'right-left':
        if (type === 'in') { fromVars.xPercent = 100; toVars.xPercent = 0; }
        else { fromVars.xPercent = 0; toVars.xPercent = -100; } // out → 밖으로
        break;

      case 'top-bottom':
        if (type === 'in') { fromVars.yPercent = -100; toVars.yPercent = 0; }
        else { fromVars.yPercent = 0; toVars.yPercent = 100; } // out → 밖으로
        break;

      case 'bottom-top':
        if (type === 'in') { fromVars.yPercent = 100; toVars.yPercent = 0; }
        else { fromVars.yPercent = 0; toVars.yPercent = -100; } // out → 밖으로
        break;

      default:
        if (type === 'in') { fromVars.xPercent = -100; toVars.xPercent = 0; }
        else if (type === 'out') { fromVars.xPercent = 0; toVars.xPercent = 100; }
    }

    return { fromVars, toVars };

  },

  getDeviceType: function() {

    const width = window.innerWidth;

    // CSS 변수에서 값 가져오기
    const root = document.documentElement;
    const contentSize = parseInt(getComputedStyle(root).getPropertyValue("--wp--style--global--content-size"), 10) || 1280;
    const wideSize = parseInt(getComputedStyle(root).getPropertyValue("--wp--style--global--wide-size"), 10) || 1680;

    if (width <= 780) return "m";            // 모바일
    if (width <= contentSize) return "t";    // 태블릿
    if (width <= wideSize) return "w";       // 와이드
    return "p";                              // PC

  },

  controlAnimation: function(dataset, container) {

    const _ = this;
    const current = _.getDeviceType();
    const anime_st = ScrollTrigger.getById(dataset.uid);

    if (!dataset?.media?.includes(current)) { // 제외 대상 아님 → 실행
      if (!anime_st.enabled) {
        container.classList.remove('wp-block-hellopanda-gsap-disable');
        anime_st.enable();
      }
    } else {
      if (anime_st.enabled) {
        container.classList.add('wp-block-hellopanda-gsap-disable');
        setTimeout(() => {
          container.removeAttribute('style');
        }, 1);
        anime_st.disable();
      }
    }

  },

  controlTimelineAnimation: function(anime, dataset, container, items) {

    const _ = this;
    const current = _.getDeviceType();

    if (!dataset?.media?.includes(current)) { // 제외 대상 아님 → 실행
      if (!anime.enabled) {
        container.classList.remove('wp-block-hellopanda-gsap-disable');
        anime.enable();
      }
    } else {
      if (anime.enabled) {
        container.classList.add('wp-block-hellopanda-gsap-disable');
        setTimeout(() => {
          items.forEach(item => {
            item.removeAttribute('style');
          });
        }, 1);
        anime.disable();
      }
    }

  },

  controlTimelineTextAnimation: function(anime, dataset, container, splits = [], tl) {

    const _ = this;
    const current = _.getDeviceType();

    if (!dataset?.media?.includes(current)) { // 제외 대상 아님 → 실행
      if (!anime.enabled) {
        container.classList.remove('wp-block-hellopanda-gsap-disable');
        setTimeout(() => {
          splits.forEach((item) => item.run());
        }, 1);
        anime.enable();
      }
    } else {
      if (anime.enabled) {
        container.classList.add('wp-block-hellopanda-gsap-disable');
        setTimeout(() => {
          container.removeAttribute('style');
          splits.forEach((item) => item.revert());
        }, 1);
        tl.clear();
        anime.disable();
      }
    }

  },

  observerIntersecting: function(anime, dataset, container) {

    if (!dataset.scrub && (dataset.toggle === 'play-always' || dataset.toggle === 'reverse-always')) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            const rect = entry.boundingClientRect;
            const rootHeight = entry.rootBounds ? entry.rootBounds.height : window.innerHeight;

            if (rect.bottom < 0) {
              // 요소가 위로 벗어남
              if (dataset.toggle === 'reverse-always') {
                if (anime.animation) {
                  anime.animation.invalidate().progress(0);
                  // anime.animation.progress(0);
                  // anime.animation.pause();
                } else {
                  anime.invalidate().progress(0);
                  // anime.progress(0);
                  // anime.pause();
                }
              }
            } else if (rect.top > rootHeight) {
              // 요소가 아래로 벗어남
              if (dataset.toggle === 'play-always') {
                if (anime.animation) {
                  anime.animation.invalidate().progress(0);
                  // anime.animation.progress(0);
                  // anime.animation.pause();
                } else {
                  anime.invalidate().progress(0);
                  // anime.progress(0);
                  // anime.pause();
                }
              }
            }
          }
        });
      }, { threshold: 0 });
      observer.observe(container);
    }

  },

  getMaxX: function(container, items = [], direction = 1, type) {

    if (!container) return 0;

    const totalWidth = items.reduce((sum, item) => sum + item.offsetWidth, 0);
    const containerWidth = container.offsetWidth;
    let x = (totalWidth - containerWidth);

    if (x < 0) {
      if (direction === 1) {
        x = x * -1;
      } else {
        x = 0;
      }
    }

    // direction: 1이면 오른쪽 끝, -1이면 왼쪽 끝
    return x * direction;

  },

  delayPlay: function(self, delay, force) {

    clearTimeout(self.delayTimer);
    if (self.isActive || force) {
      self.animation.pause();
      self.delayTimer = setTimeout(() => self.animation.play(), delay * 1000);
    } else {
      self.animation.pause();
    }

  },

  isInViewport: function(el) {

    if (!el) return false;
    const rect = el.getBoundingClientRect();
    return (
      rect.top < window.innerHeight &&   // 위가 화면 아래보다 위에 있음
      rect.bottom > 0 &&                 // 아래가 화면 위보다 아래에 있음
      rect.left < window.innerWidth &&   // 좌측이 화면 우측보다 왼쪽에 있음
      rect.right > 0                     // 우측이 화면 좌측보다 오른쪽에 있음
    );

  },

  /**
   * 모든 새로고침의 핵심
   * @param {*} self // gsap 요소
   * @param {*} expand // 작동 방식 힌트 'top', 'scroll'
   */
  isPlayInfo: function(self, expand, hook = '', origin) {

    const _ = this;

    const toggleActions = (self.vars.toggleActions || "play none none none").split(' ');
    const scroll = window.scrollY;

    const start = self.start; // trigger start 위치(px)
    const end = self.end;     // trigger end 위치(px)

    let shouldBeActive = false;

    // 아래로 내려와서 실행되는 경우
    if (scroll >= start && toggleActions[0] === "play" && toggleActions[2] === "none") {
      shouldBeActive = true;
    }

    // 위로 올라가서 실행되는 경우
    if (scroll <= end && toggleActions[0] === "none" && toggleActions[2] === "play") {
      shouldBeActive = true;
    }

    // if (expand === 'refresh') {

    //   // console.log(self.trigger.innerText.slice(0, 15), self);

    //   if (self.isActive || shouldBeActive) {
    //     self.animation?.scrollTrigger?.refresh?.();
    //     self.animation.invalidate().progress(1);
    //   } else {
    //     self.animation.invalidate().progress(0);
    //   }

    //   return;

    // }

    if (expand === 'layer' && hook === 'onLeaveBack') {

      _.selfConsole(self, 'layer - onLeaveBack', 'pink');
      self.animation.scrollTrigger.enable();

    }

    // 스크롤 특이사항
    if (hook === 'onLeaveBack') {

      // console.log('onLeaveBack', self.trigger.innerText.slice(0, 15), self);
      // _.selfConsole(self, 'onLeaveBack', '');

      if (!self.enabled) {

        _.selfConsole(self, 'onLeaveBack - 멈춘거 해제', '');
        self.animation.scrollTrigger.enable();
        self.animation?.scrollTrigger?.refresh?.();
        self.animation.invalidate().progress(1);

      } else if (self.__exception === 'refresh') {

        _.selfConsole(self, 'onLeaveBack - 색상 모드 변환했으므로 컨트롤 안함', '');
        delete self.__exception;

      } else {

        _.selfConsole(self, 'onLeaveBack - else', '');

        self.animation.invalidate();

        if (!self.vars?.dataset?.type) {

          self.animation?.scrollTrigger?.refresh?.();
          self.animation.invalidate().progress(1);

        } else {

          // 최상위로 갔다면 처리하지 마라.
          // 트리커 스크롤의 반 보다 클때만 처리
          requestAnimationFrame(() => {
            if (window.scrollY > self.start * .5) {
              if (self.animation.progress() === 0) {
                self.animation?.scrollTrigger?.refresh?.();
                self.animation.invalidate().progress(1);
              }
            } else {
              // console.log('처리안함')
            }
          });

        }

      }

      return;

    }

    // timeline play/pause 제어는 ScrollTrigger 활성 상태 기준으로
    if (expand === 'top' && self.vars?.scrub && self.vars?.splits) {

      // console.log('top', self.trigger.innerText.slice(0, 15), self);
      _.selfConsole(self, 'top', '');

      // 탑으로 이동했고, 휠은 스크럽으로 연결됐고, 
      // 내부 텍스트 객체가 있을 때 from 상태로 돌려준다.
      self.vars.splits.forEach((item) => item.from());

    } else if (self.isActive || shouldBeActive) {

      if (expand === 'top') {

        // console.log('on - top', self.trigger.innerText.slice(0, 15), self);
        _.selfConsole(self, 'off - top', '');
        self.animation.invalidate().progress(1);

      } else if (expand === 'scroll') {

        // console.log('on - scroll', self.trigger.innerText.slice(0, 15), self);
        _.selfConsole(self, 'on - scroll', '');

        // self.refresh();       // start/end 재계산
        // self.update(true);    // 새 위치 기준으로 상태 반영

        // 스크롤 내부의 텍스트
        // self.animation?.scrollTrigger?.refresh?.();
        // self.animation.invalidate().progress(1);

        // // textSplit 대처
        // if (self.vars?.splits) {
        //   self.vars.splits.forEach((item) => item.to());
        // }

      } else if (expand === 'onload') {

        _.selfConsole(self, 'on - onload', 'purple');

        if (self.vars?.splits) {
          self.animation.scrollTrigger.disable();
          self.animation.invalidate().progress(1);
        }

        // self.animation?.scrollTrigger?.refresh?.();
        // self.animation.invalidate().progress(1);

        // 스크롤 내부의 텍스트
        if (self.vars?.splits) {
          // console.log('1로 보냈음요..')
          // self.vars.splits.forEach((item) => item.run());
          // self.animation.invalidate().progress(1);
        }

      } else {

        // console.log('on - else', self.trigger.innerText.slice(0, 15), self);
        _.selfConsole(self, 'on - else', 'purple');

        if (!self.vars?.dataset?.startElement === 'scroll') {
          self.refresh();
        }

        // self.animation?.scrollTrigger?.refresh?.();
        // self.animation.invalidate().progress(1);

        // textSplit 대처
        // if (!expand === 'top' && self.vars?.splits) {
        //   self.vars.splits.forEach((item) => item.to());
        // }
      }

    } else {

      if (expand === 'top') {

        // console.log('off - top', self.trigger.innerText.slice(0, 15), self);
        _.selfConsole(self, 'off - top', '');

        self.animation.invalidate().progress(0).pause();
        self.animation?.scrollTrigger?.refresh?.();

        if (self.vars?.splits) {
          self.vars.splits.forEach((item) => item.from());
        }

      } else if (expand === 'scroll') {

        // console.log('off - scroll', self.trigger.innerText.slice(0, 15), self);
        _.selfConsole(self, 'off - scroll', '');
        // 아무것도 안해?

        self.animation?.scrollTrigger?.refresh?.();

      } else {
        
        // console.log('off - else', self.trigger.innerText.slice(0, 15), self);
        _.selfConsole(self, 'off - else', '');

        self.animation?.scrollTrigger?.refresh?.();
        self.animation.invalidate().progress(0);

        // textSplit 대처
        // if (self.vars?.splits) {
        //   self.vars.splits.forEach((item) => item.from());
        // }
      }

    }
  },

  pinInsideParentRefresh: function() {
    
    return;
    
    const _ = this;

    ScrollTrigger.getAll().forEach(st => {

      if (st.vars?.dataset?.startElement === 'scroll' && !st.vars?.dataset?.type) {
        // st.refresh();
        _.selfConsole(st, 'meet - top - scroll', 'red');
        st.animation?.scrollTrigger?.refresh?.();
      }

      // if (!st.vars?.dataset?.startElement === 'scroll' && st.vars?.dataset?.type === 'text') {
      //   _.selfConsole(st, 'meet - top - text', 'brown');
      //   st.vars?.splits?.forEach((item) => item.from());
      //   st.animation?.scrollTrigger?.refresh?.();
      //   st.animation.invalidate().progress(0);
      // }

    });

  },

  pinInsideSonLayerRefresh: function(self) {

    const _ = this;

    // 그룹이라면 좌표를 다시 계산할 필요가 없다.
    let target = '.wp-block-hellopanda-gsap-block[data-gsap-uid] .wp-block-hellopanda-gsap-block-cover';

    const sonsArray = (self.spacer || self.trigger)?.querySelectorAll(target) || [];
    const sons = Array.from(sonsArray);
    ScrollTrigger.getAll().forEach(st => {
      if (st.trigger && sons.includes(st.trigger)) {

        // console.log('자식 업데이트함', st.trigger.innerText.slice(0, 15), self);

        // st.animation?.scrollTrigger?.refresh?.();
        self.update(true);
        st.update(true);
        _.isPlayInfo(st, 'layer', 'onLeaveBack', self);

      }
    });

  },

  pinInsideSonRefresh: function(self, expand = '', hook = '') {

    const _ = this;

    // 그룹이라면 좌표를 다시 계산할 필요가 없다.
    let target = '.wp-block-hellopanda-gsap-block[data-gsap-uid] .wp-block-hellopanda-gsap-block-cover';
    if (self.vars?.dataset?.makegroup && !self.enabled) { // 그룹은 다시 계산하지 않지만 강제로 멈춰놨다면 읽어라.
      target = '.wp-block-hellopanda-gsap-block[data-gsap-uid] .wp-block-hellopanda-gsap-group-block .wp-block-hellopanda-gsap-block-cover';
    }

    const sonsArray = (self.spacer || self.trigger)?.querySelectorAll(target) || [];
    const sons = Array.from(sonsArray);
    ScrollTrigger.getAll().forEach(st => {
      if (st.trigger && sons.includes(st.trigger)) {

        // console.log('자식 업데이트함', st.trigger.innerText.slice(0, 15), self);

        // st.animation?.scrollTrigger?.refresh?.();
        self.update(true);
        st.update(true);
        _.isPlayInfo(st, expand, hook, self);

      }
    });

  },

  setLayerGroupHeight: function(self, gsapContainer, items, dataset) {
  
    // 레이어 높이 자동 지정
    if (dataset.layerHeight) {

      // 이미 지정했다면 비워줘라.
      if (self.__maxHeight) {
        items.forEach(item => {
          item.style.height = 'auto';
        });
      }

      // 초기화 하고
      self.__maxHeight = 0;
      items.forEach(item => {
        if (!item.scrollHeight) {
          return;
        }
        if (item.scrollHeight > self.__maxHeight) self.__maxHeight = item.scrollHeight;
        item.setAttribute('gsap-layer-height-align', dataset.layerHeightAlign);
      });
      gsapContainer.style.height = self.__maxHeight + "px";
      items.forEach(item => {
        item.style.height = self.__maxHeight + "px";
      });

    }

  },

  wrapChildrenByClass: function(container, childClass, wrapperClass) {
    if (!container) return;

    const children = Array.from(container.querySelectorAll(`:scope > .${childClass}`));
    if (children.length === 0) return;

    let wrapper = null;

    children.forEach(child => {
      // wrapper가 아직 없으면 생성 후 child 앞에 삽입
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = wrapperClass;
        container.insertBefore(wrapper, child);
      }

      // child를 wrapper 안으로 이동
      wrapper.appendChild(child);
    });
  },

  colorRefresh: function() {

    const _ = this;
    
    _.refresh();

    return;

    const scroll = window.scrollY;

    requestAnimationFrame(() => {
      ScrollTrigger.getAll().forEach(self => {

        if (self.vars?.dataset?.type === 'text') {
          if (self.vars.dataset.fromTextColor || self.vars.dataset.fromBgColor || self.vars.dataset.toTextColor || self.vars.dataset.toBgColor) {

            _.selfConsole(self, 'colormode', 'blue');

            console.log(self.animation.progress())
            if (self.animation.progress() === 1) {
              self.animation.invalidate();
              self.update(true);
            } else {
              self.refresh();
            }

            // let shouldBeActive = false;
            // const toggleActions = (self.vars.toggleActions || "play none none none").split(' ');
            // const start = self.start; // trigger start 위치(px)
            // const end = self.end;     // trigger end 위치(px)

            // // 아래로 내려와서 실행되는 경우
            // if (scroll >= start && toggleActions[0] === "play" && toggleActions[2] === "none") {
            //   shouldBeActive = true;
            // }

            // // 위로 올라가서 실행되는 경우
            // if (scroll <= end && toggleActions[0] === "none" && toggleActions[2] === "play") {
            //   shouldBeActive = true;
            // }

            // const progress = self.animation.progress() || 0;
            // self.animation?.scrollTrigger?.refresh?.();
            // self.vars?.splits?.forEach((item) => item.from() );
            // self.animation.invalidate().progress( progress );

            // 표시해둔다.
            self.__exception = 'refresh';

          }
        }

      });
    });

  },

  onloadRefresh: function() {

    const _ = this;

    ScrollTrigger.sort();
    // ScrollTrigger.refresh();

    requestAnimationFrame(() => {
      ScrollTrigger.getAll().forEach(self => {

        const hasPinnedParent = self.trigger.closest('.wp-block-hellopanda-gsap-block[data-gsap-pin="true"]');
        if (hasPinnedParent && self.vars?.dataset?.type === 'text') {
          _.isPlayInfo(self, 'onload');
        }

      });
    });

  },

  refresh: function() {

    const _ = this;
    if (!ScrollTrigger) return;

    const currentY = window.scrollY; // 현재 위치 저장
    const pin_array = [];
    const text_array = [];
    const base_array = [];

    // 1. 강제로 상단 이동
    // if (_.isComplicated) {
      window.scrollTo(0, 0);
      document.body.style.opacity = '0';
      document.body.style.pointerEvents = 'none';
    // }

    requestAnimationFrame(() => {

      // 2. ScrollTrigger 정렬 및 refresh
      ScrollTrigger.sort();
      ScrollTrigger.getAll().forEach((st, index) => {

        // 부모에 핀이 없는 것만 처리
        // const hasPinnedParent = st.trigger.closest('.wp-block-hellopanda-gsap-block[data-gsap-pin="true"]');
        // if (hasPinnedParent) {
        //   console.log(index, '실행')
        //   _.pinInsideSonRefresh(st, 'refresh');
        // }

        if (st.pin) {
          pin_array.push(st);
        } else if (st.vars?.dataset?.type === 'text') {
          st.vars?.animation?.clear?.();
          st.vars?.splits?.forEach((item) => item.run());
          text_array.push({
            st,
            progress: st.animation.progress()
          });
        } else {
          base_array.push(st);
        }

        if (st.vars.dataset.layerHeight) {
          st.refresh();
        }

        

      });

      // 3. 원래 위치로 되돌리기
      requestAnimationFrame(() => {

        // 핀 종류 업데이트 하고
        if (pin_array.length) {         
          requestAnimationFrame(() => { 
            pin_array.forEach(st => {
              st.animation?.scrollTrigger?.refresh?.();
            });
          });
        }

        // 텍스트 종료 업데이트 하고
        if (text_array.length) {
          requestAnimationFrame(() => { 
            text_array.forEach(arr => {
              arr.st.animation.invalidate().progress(0);
              arr.st.animation?.scrollTrigger?.refresh?.();
              arr.st.animation.invalidate().progress(arr.progress);
            });
          });
        }

        // 베이스 종류 업데이트
        if (base_array.length) {         
          requestAnimationFrame(() => { 
            base_array.forEach(st => {
              st.animation?.scrollTrigger?.refresh?.();
            });
          });
        }

        // // 레이어 종류 업데이트
        // if (layer_array.length) {         
        //   requestAnimationFrame(() => { 
        //     layer_array.forEach(st => {

        //       console.log(st)
        //       // setLayerGroupHeight: function(self, gsapContainer, items, dataset) {

        //     });
        //   });
        // }

        // 스크롤을 옯겨준다.
        // if (_.isComplicated) {
          requestAnimationFrame(() => { 
            window.scrollTo(0, currentY);
            document.body.style.opacity = '';
            document.body.style.pointerEvents = '';
          });
        // }
      });

      console.log('%cGSAP - refresh completed', 'font-size:bold;color:yellow;');
    });

  },

  gsap: function(container) {

    const _ = this;

    const pinspacer = container.closest('.wp-block-hellopanda-gsap-block-pinspacer');
    const cover = container.closest('.wp-block-hellopanda-gsap-block-cover');
    const dataset = _.getDataset(container);
    const { gsapFrom, gsapTo } = _.setDataset(container, dataset);

    if (dataset.overflow) {
      cover.classList.add('wp-block-hellopanda-gsap-block-cover-mask')
    }
    if (dataset.overflowInner) {
      container.classList.add('wp-block-hellopanda-gsap-block-mask')
    }

    // 내부 블록 처리
    if (!dataset.type) {

      // 스크롤 트리거
      gsapTo.scrollTrigger = {
        dataset,
        gsapFrom,
        gsapTo,
        id: dataset.uid, // id를 줘야 나중에 정확히 kill 가능
        trigger: cover,
        // start: `${dataset.startElement || 'top'}${_.getStartOffset(dataset)} ${dataset.startViewport || 'bottom'}`,
        start: dataset.startElement === 'scroll' ? () => window.scrollY + 1 + $.getNumber(dataset.startOffset) : `${dataset.startElement || 'top'}${_.getStartOffset(dataset)} ${dataset.startViewport || 'bottom'}`,
        end: dataset.endElement === 'max' ? 'max' : `${dataset.endElement || 'bottom'}${_.getEndOffset(dataset)} ${dataset.endViewport || 'top'}`,

        pin: dataset.pin ? dataset.pin : false,
        pinSpacing: dataset.pinspace ? dataset.pinspace : false,
        pinSpacer: dataset.pinspacer ? cover.closest('.wp-block-hellopanda-gsap-block-pinspacer') || false : false,
        scrub: dataset.scrub || dataset.scrub === '0' ? dataset.scrub : false,
        toggleActions: _.getToggleActions(dataset),

        markers: dataset.markers ?? false,
        invalidateOnRefresh: true,
        resizeObserver: true,
        anticipatePin: dataset.pinfix ? dataset.pinfix : 0,
        onRefresh: (self) => {

          //

        },
        onEnter: (self) => {

          // 지연시간 ( 기본, 스크롤은 지원 안되기 때문에 수동 설정 )
          if (gsapTo.delay && !dataset.scrub) {
            _.delayPlay(self, gsapTo.delay);
          }

        },
      }

      // 초기 상태 강제 적용
      gsap.set(container, gsapFrom);

      const anime = gsap.fromTo(container, gsapFrom, gsapTo);

      if (!dataset.media) {

        // 유지

      } else {

        // 기본 등록
        setTimeout(() => {
          _.controlAnimation(dataset, container);
        }, 1);

        // 전역 resize 감지 구독
        _.resizeSubscribe(() => {
          _.controlAnimation(dataset, container);
        });

      }

      // 화면 벗어남을 감지해서 되돌리기
      // _.observerIntersecting(anime, dataset, cover);

    } else if (dataset.type === 'text') {

      let isSet = null;

      const tl = gsap.timeline();
      const targets = container.querySelectorAll(':scope > .wp-block-hellopanda-gsap-item-text');
      const items = gsap.utils.toArray(targets);
      const splits = items.map((item) => {
        const stagger = isNaN(parseFloat(item.dataset.gsapStagger)) ? 0.05 : parseFloat(item.dataset.gsapStagger) ;
        const splitchars = item.dataset.gsapSplitchars || '';
        const splitwords = item.dataset.gsapSplitwords || '';
        const splitlines = item.dataset.gsapSplitlines || '';
        const splitValue = [
          splitlines === 'true' ? 'lines' : null,
          splitwords === 'true' ? 'words' : null,
          splitchars === 'true' ? 'chars' : null
        ].filter(Boolean);
        const types = splitValue.length ? splitValue : ['chars'];
        const from = item.dataset.gsapFrom || 'start';

        return {
          el: item,
          stagger: stagger,
          // from: from,
          split: null,

          clear() {

            tl.clear();

          },

          run() {

            // 기존 split 있으면 revert
            if (this.split) {
              this.split.revert();
              // return;
            }

            // 새 split 생성
            this.split = new SplitText(this.el, { 
              types,
            });

            // 애니메이션
            types.forEach(type => {
              if (this.split[type]) {
                tl.fromTo(
                  this.split[type], 
                  {
                    ...gsapFrom, 
                  },
                  { 
                    ...gsapTo, 
                    stagger: {
                      // amount: dataset.duration || null,
                      each: this.stagger, // 요소 간 간격
                      from: from      // 랜덤 순서
                    },
                  }, 
                  '>0'
                );
              }
            });

            // immediateRender 강제 적용
            if (!isSet && gsapFrom.immediateRender) {
              setTimeout(() => {
                if (splitchars || !splitValue.length) gsap.set(gsap.utils.toArray(this.split.chars), { ...gsapFrom });
                if (splitwords) gsap.set(gsap.utils.toArray(this.split.words), { ...gsapFrom });
                if (splitlines) gsap.set(gsap.utils.toArray(this.split.lines), { ...gsapFrom });
                isSet = true;
              }, 1);
            }
            
          },
          revert() {
            if (this.split) {
              this.split.revert();
              this.split = null;
            }
          },
          from() {

            // immediateRender 강제 적용
            //if (gsapFrom.immediateRender) {
              if (splitchars || !splitValue.length) gsap.set(gsap.utils.toArray(this.split.chars), { ...gsapFrom });
              if (splitwords) gsap.set(gsap.utils.toArray(this.split.words), { ...gsapFrom });
              if (splitlines) gsap.set(gsap.utils.toArray(this.split.lines), { ...gsapFrom });
            //}

          },
          to() {

            // immediateRender 강제 적용
            //if (gsapTo.immediateRender) {
              if (splitchars || !splitValue.length) gsap.set(gsap.utils.toArray(this.split.chars), { ...gsapTo });
              if (splitwords) gsap.set(gsap.utils.toArray(this.split.words), { ...gsapTo });
              if (splitlines) gsap.set(gsap.utils.toArray(this.split.lines), { ...gsapTo });
            //}

          },
        };
      });

      const params = {
        dataset,
        gsapFrom,
        gsapTo,
        splits,
        id: dataset.uid, // id를 줘야 나중에 정확히 kill 가능
        animation: tl,
        trigger: cover,
        // start: `${dataset.startElement || 'top'}${_.getStartOffset(dataset)} ${dataset.startViewport || 'bottom'}`,
        start: dataset.startElement === 'scroll' ? () => window.scrollY + 1 + $.getNumber(dataset.startOffset) : `${dataset.startElement || 'top'}${_.getStartOffset(dataset)} ${dataset.startViewport || 'bottom'}`,
        end: dataset.endElement === 'max' ? 'max' : `${dataset.endElement || 'bottom'}${_.getEndOffset(dataset)} ${dataset.endViewport || 'top'}`,

        pin: dataset.pin ? dataset.pin : false,
        pinSpacing: dataset.pinspace ? dataset.pinspace : false,
        pinSpacer: dataset.pinspacer ? cover.closest('.wp-block-hellopanda-gsap-block-pinspacer') || false : false,
        scrub: dataset.scrub || dataset.scrub === '0' ? dataset.scrub : false,
        toggleActions: _.getToggleActions(dataset),

        markers: dataset.markers ?? false,
        invalidateOnRefresh: true,
        resizeObserver: true,
        anticipatePin: dataset.pinfix ? dataset.pinfix : 0,
        onRefresh: (self) => {

          //

        },
        // onLeave: (self) => {
        //   if (dataset.markers) {
        //     console.log('아래로 벗어남')
        //   }
        // },
        // onLeaveBack: (self) => {
        //   if (dataset.markers) {
        //     console.log('위로 벗어남')
        //   }
        // }
      }

      // 최초 실행
      splits.forEach((item) => item.run());

      const anime = ScrollTrigger.create(params);

      if (!dataset.media) {

        // 유지

      } else {

        // 기본 등록
        setTimeout(() => {
          _.controlTimelineTextAnimation(anime, dataset, container, splits, tl);
        }, 1);

        // 전역 resize 감지 구독
        _.resizeSubscribe(() => {
          _.controlTimelineTextAnimation(anime, dataset, container, splits, tl);
        });

      }

      // 화면 벗어남을 감지해서 되돌리기
      // _.observerIntersecting(anime, dataset, cover);

    } else if (dataset.type === 'layer') {

      // 블록 그룹화
      if (dataset.makegroup) {
        _.wrapChildrenByClass(container, 'wp-block-hellopanda-gsap-item-block', 'wp-block-hellopanda-gsap-group-block');
      }
      const gsapContainer = dataset.makegroup ? container.querySelector(':scope > .wp-block-hellopanda-gsap-group-block') : container ;

      const tl = gsap.timeline({
        onReverseComplete: function() {

          //

        },
      });
      if (dataset.makegroup) {
        tl.fromTo(
          gsapContainer,
          { x: 0, y: 0, autoAlpha: 1 },
          { x: 0, y: 0, autoAlpha: 1, duration: 0 }
        );
      }

      const targets = gsapContainer.querySelectorAll(':scope > .wp-block-hellopanda-gsap-item-block');
      const items = gsap.utils.toArray(targets);
      const params = {
        dataset,
        gsapFrom,
        gsapTo,
        id: dataset.uid, // id를 줘야 나중에 정확히 kill 가능
        animation: tl,
        trigger: cover,
        // start: `${dataset.startElement || 'top'}${_.getStartOffset(dataset)} ${dataset.startViewport || 'bottom'}`,
        start: dataset.startElement === 'scroll' ? () => window.scrollY + 1 + $.getNumber(dataset.startOffset) : `${dataset.startElement || 'top'}${_.getStartOffset(dataset)} ${dataset.startViewport || 'bottom'}`,
        end: dataset.endElement === 'max' ? 'max' : `${dataset.endElement || 'bottom'}${_.getEndOffset(dataset)} ${dataset.endViewport || 'top'}`,

        pin: dataset.pin ? dataset.pin : false,
        pinSpacing: dataset.pinspace ? dataset.pinspace : false,
        pinSpacer: dataset.pinspacer ? cover.closest('.wp-block-hellopanda-gsap-block-pinspacer') || false : false,
        scrub: dataset.scrub || dataset.scrub === '0' ? dataset.scrub : false,
        toggleActions: _.getToggleActions(dataset),

        markers: dataset.markers ?? false,
        invalidateOnRefresh: true,
        resizeObserver: true,
        anticipatePin: dataset.pinfix ? dataset.pinfix : 0,
        onRefresh: (self) => {

          // 레이어 높이 자동 지정
          _.setLayerGroupHeight(self, gsapContainer, items, dataset);

        },
        onLeaveBack: (self) => {

          // 자식들 새로고침
          if (dataset.pin) {
            _.pinInsideSonLayerRefresh(self);
          }

        },
        // onLeave: (self) => {

        //   // 자식들 새로고침
        //   if (dataset.pin) {
        //     _.pinInsideSonRefresh(self, 'onLeave');
        //   }

        // },
        // onLeaveBack: (self) => {

        //   // 자식들 새로고침
        //   if (dataset.pin) {
        //     _.pinInsideSonRefresh(self, 'onLeaveBack');
        //   }

        // },
      }

      // 순서대로 from 애니메이션 추가
      items.forEach((item, i) => {

        const itemDataset = _.getDataset(item);
        const { fromVars, toVars } = _.getGsapVars(item);
        const zIndex = ['in', 'clippath'].includes(itemDataset.type) || !itemDataset.type ? items.length + i : items.length + (i * -1) ;

        if (itemDataset.onlyanime) {
          // 강제 실행
        } else if (!fromVars.xPercent && !fromVars.yPercent && !fromVars.clipPath && !toVars.xPercent && !toVars.yPercent && !toVars.clipPath) {
          // 페이드 아웃 설정
          if (itemDataset.fadeout) {
            tl.to(
              item, 
              { opacity: 0 }, 
            );
          }
          // 딜레이 설정
          if (dataset.delay) {
            tl.fromTo(
              gsapContainer,
              { x: 0, y: 0, autoAlpha: 1 },
              { x: 0, y: 0, autoAlpha: 1, duration: dataset.delay }
            );
          }
          return;
        }

        // scrub 있을 때는 delay 제거
        if (dataset.scrub) {
          delete gsapFrom.delay;
          delete gsapTo.delay;
        }

        // 타이밍 제어
        let timingStart = null;
        let timingEnd = null;
        if (itemDataset.timingStart && itemDataset.timingStart < 0) {
          timingStart = `-=${itemDataset.timingStart * -1}`;
        } else if (itemDataset.timingStart && itemDataset.timingStart > 0) {
          timingStart = `+=${itemDataset.timingStart}`;
        }
        if (itemDataset.timingEnd) {
          timingEnd = `+=${itemDataset.timingEnd}`;
        }

        // 페이드 효과 추가
        tl.fromTo(item, { 
          ...gsapFrom,
          ...fromVars,
          zIndex,
        },{ 
          ...gsapTo,
          ...toVars,
          zIndex,
          onStart: function() {
            item.setAttribute('data-gsap-active', 'true');
          },
          onReverseComplete: function() {
            item.removeAttribute('data-gsap-active');
          },
        }, timingStart );
        
        // 종료 지연
        if (timingEnd) {
          tl.to(
            item, 
            { opacity: 0 }, 
            timingEnd
          );
        }

        // 페이드 아웃 설정
        if (itemDataset.fadeout) {
          tl.to(
            item, 
            { opacity: 0 }, 
          );
        }

        // 딜레이 추가
        if (dataset.delay) {
          tl.fromTo(
            gsapContainer,
            { x: 0, y: 0, autoAlpha: 1 },
            { x: 0, y: 0, autoAlpha: 1, duration: dataset.delay }
          );
        }

      });

      const anime = ScrollTrigger.create(params);

      if (!dataset.media) {

        // 유지

      } else {

        // 기본 등록
        setTimeout(() => {
          _.controlTimelineAnimation(anime, dataset, container, items);
        }, 1);

        // 전역 resize 감지 구독
        _.resizeSubscribe(() => {

            _.controlTimelineAnimation(anime, dataset, container, items);

            // 레이어 높이 자동 지정
            _.setLayerGroupHeight(self, gsapContainer, items, dataset);

        });

      }

      // 화면 벗어남을 감지해서 되돌리기
      // _.observerIntersecting(anime, dataset, cover);

    } else if (dataset.type === 'scroll') {

      // 블록 그룹화
      if (dataset.makegroup) {
        _.wrapChildrenByClass(container, 'wp-block-hellopanda-gsap-item-block', 'wp-block-hellopanda-gsap-group-block');
      }
      const gsapContainer = dataset.makegroup ? container.querySelector(':scope > .wp-block-hellopanda-gsap-group-block') : container ;
      const targets = gsapContainer.querySelectorAll(':scope > .wp-block-hellopanda-gsap-item-block');
      const items = gsap.utils.toArray(targets);

      // 스크롤 트리거
      gsapTo.scrollTrigger = {
        dataset,
        gsapFrom,
        gsapTo,
        id: dataset.uid, // id를 줘야 나중에 정확히 kill 가능
        trigger: cover,
        // start: `${dataset.startElement || 'top'}${_.getStartOffset(dataset)} ${dataset.startViewport || 'bottom'}`,
        start: dataset.startElement === 'scroll' ? () => window.scrollY + 1 + $.getNumber(dataset.startOffset) : `${dataset.startElement || 'top'}${_.getStartOffset(dataset)} ${dataset.startViewport || 'bottom'}`,
        end: dataset.endElement === 'max' ? 'max' : `${dataset.endElement || 'bottom'}${_.getEndOffset(dataset)} ${dataset.endViewport || 'top'}`,

        pin: dataset.pin ? dataset.pin : false,
        pinSpacing: dataset.pinspace ? dataset.pinspace : false,
        pinSpacer: dataset.pinspacer ? cover.closest('.wp-block-hellopanda-gsap-block-pinspacer') || false : false,
        scrub: dataset.scrub || dataset.scrub === '0' ? dataset.scrub : false,
        toggleActions: _.getToggleActions(dataset),

        markers: dataset.markers ?? false,
        invalidateOnRefresh: true,
        resizeObserver: true,
        anticipatePin: dataset.pinfix ? dataset.pinfix : 0,
        onRefresh: (self) => {

          // 새로고침 보정
          // if (gsapFrom.immediateRender) {
          //   _.isPlayInfo(self);
          // }

        },
        onEnter: (self) => {

          // 지연시간 ( 기본, 스크롤은 지원 안되기 때문에 수동 설정 )
          if (gsapTo.delay && !dataset.scrub) {
            _.delayPlay(self, gsapTo.delay);
          }

        },
        onLeave: (self) => {

          // 자식들 새로고침
          // if (dataset.pin) {
          //   _.pinInsideSonRefresh(self, 'scroll');
          // }

        },
        onLeaveBack: (self) => {

          // console.log('zz', self)

          // 자식들 새로고침
          // if (dataset.pin) {
          //   _.pinInsideSonRefresh(self, 'scroll', 'onLeaveBack');
          // }

        },
      }

      // 최대 X 위치를 설정
      const fromX = gsapFrom.x; // 기존 값 저장
      const toX = gsapTo.x;
      gsapFrom.x = () => {
        if (fromX === '1000%') return _.getMaxX(gsapContainer, items, 1, 'from');
        if (fromX === '-1000%') return _.getMaxX(gsapContainer, items, -1, 'from');
        return fromX;
      };
      gsapTo.x = () => {
        if (toX === '1000%') return _.getMaxX(gsapContainer, items, 1, 'to');
        if (toX === '-1000%') return _.getMaxX(gsapContainer, items, -1, 'to');
        return toX;
      };

      // 초기 상태 강제 적용
      gsap.set(container, gsapFrom);

      // const anime = gsap.fromTo(gsapContainer, gsapFrom, gsapTo);
      const anime = gsap.fromTo(
        gsapContainer,
        gsapFrom,
        {
          ...gsapTo,
          onReverseComplete: () => {

            // 자식들 새로고침
            if (dataset.pin) {
              _.pinInsideSonRefresh(anime.scrollTrigger, 'scroll', 'onLeaveBack');
            }

          }
        }
      );

      if (!dataset.media) {

        // 유지

      } else {

        // 기본 등록
        setTimeout(() => {
          _.controlAnimation(dataset, container);
        }, 1);

        // 전역 resize 감지 구독
        _.resizeSubscribe(() => {
          _.controlAnimation(dataset, container);
        });

      }

      // 화면 벗어남을 감지해서 되돌리기
      // _.observerIntersecting(anime, dataset, cover);

    }

    // 복잡도 등록
    if (dataset.pin) {
      _.isComplicated = true;
    }

    // 커버 이미지 처리
    if (dataset.coverColor || dataset.coverGradient) {
      // 이미 overlay가 있으면 중복 생성 방지
      let overlay = container.querySelector('.hellopanda-cover-overlay');

      if (!overlay) {
        overlay = document.createElement('div');
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.right = '0';
        overlay.style.bottom = '0';
        overlay.style.left = '0';
        overlay.style.opacity = dataset.coverOpacity || 1;
        container.prepend(overlay); // 최상위에 추가
      }

      // 색상 / 그라디언트 적용
      if (dataset.coverGradient) {
        overlay.style.backgroundImage = dataset.coverGradient;
        overlay.style.backgroundColor = '';
      } else if (dataset.coverColor) {
        overlay.style.backgroundColor = dataset.coverColor;
        overlay.style.backgroundImage = '';
      }
    }

    // 화면 로드 후 표현
    if (dataset.ready) {
      setTimeout(() => {
        cover?.removeAttribute('data-gsap-ready');
        pinspacer?.removeAttribute('data-gsap-ready');
        container?.removeAttribute('data-gsap-ready');
      }, 1);
    }

    // 개발자 모드
    if (dataset.markers && $.isDevelopment()) {
      console.log('dataset', dataset);
      console.log('gsapFrom', gsapFrom);
      console.log('gsapTo', gsapTo);
    }

    return;

  },

  selfConsole: function(self = null, text = '빈 값', color = 'inherit') {

    return;

    console.log(
      "%c" + text,
      "color:"+ color,   // ✅ 스타일은 두 번째 인자
      self ? self?.trigger?.innerText?.slice(0, 30) : 'self 값 없음',
      self ? self : '빈 값'
    );

  }

};
HELLOPANDA_GSAP.init();